import { useMemo, useState } from 'react';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    type MemoryBackfillOrganization,
    useMemoryBackfill,
} from '@apps/web/features/admin_agent_notes/model/use_memory_backfill';
import { type components } from '@libraries/typescript/api_client';

type AgentNoteDomain = components['schemas']['AgentNoteDomain'];
type MembershipAction = 'enroll' | 'remove' | 'park' | 'unpark';
type MembershipTarget = {
    readonly action: MembershipAction;
    readonly organization: MemoryBackfillOrganization;
};

type DomainOption = {
    readonly value: AgentNoteDomain;
    readonly label: string;
};

const DOMAIN_OPTIONS: Array<DomainOption> = [
    { value: 'availability', label: 'Availability' },
    { value: 'memory', label: 'Memory' },
    {
        value: 'client_caregiver_preference',
        label: 'Client-caregiver preference',
    },
    { value: 'outreach_filter', label: 'Outreach filter' },
];

const MEMBERSHIP_LABELS: Record<MembershipAction, string> = {
    enroll: 'Enroll',
    remove: 'Remove',
    park: 'Park',
    unpark: 'Unpark',
};

function blockedOrganizationRow(
    organizationId: string,
    organizationName: string | null | undefined,
): MemoryBackfillOrganization {
    return {
        organization_id: organizationId,
        organization_name: organizationName ?? null,
        enrolled: false,
        park_new_items: false,
        eligible: 0,
        notes_created: 0,
        in_flight: 0,
        parked_items: 0,
        parked_notes: 0,
        queued_reclassification_count: 0,
        stranded_parked_item_count: 0,
        terminal_parked_awaiting_absorption: 0,
        terminal: 0,
        absorbed: 0,
        failed: 0,
        blocked_reason: 'expanded_memory_inputs_disabled',
        classifier_prompt_sha256_counts: [],
    };
}

function organizationLabel(organization: MemoryBackfillOrganization): string {
    return organization.organization_name ?? organization.organization_id;
}

function parkedItemsHref(organizationId: string): string {
    const search = new URLSearchParams({
        organization_id: organizationId,
        source: 'memory_backfill',
        parked: 'true',
    });
    return `/admin/agent-notes?${search.toString()}`;
}

function zeroItemsHref(organizationId: string): string {
    const search = new URLSearchParams({
        organization_id: organizationId,
        source: 'memory_backfill',
        park_items: 'true',
        zero_items: 'true',
    });
    return `/admin/agent-notes?${search.toString()}`;
}

function PromptCounts({
    organization,
}: {
    readonly organization: MemoryBackfillOrganization;
}) {
    if (organization.classifier_prompt_sha256_counts.length === 0) {
        return <span className="text-muted-foreground">None</span>;
    }
    return (
        <span className="flex max-w-64 flex-col whitespace-normal">
            {organization.classifier_prompt_sha256_counts.map((count) => (
                <span
                    key={count.classifier_prompt_sha256 ?? 'not-recorded'}
                    className="font-mono text-xs"
                >
                    {count.classifier_prompt_sha256 ?? 'Not recorded'}:{' '}
                    {count.note_count}
                </span>
            ))}
        </span>
    );
}

function ProgressTable({
    organizations,
}: {
    readonly organizations: Array<MemoryBackfillOrganization>;
}) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Enrollment</TableHead>
                    <TableHead>Park new notes</TableHead>
                    <TableHead>Eligible</TableHead>
                    <TableHead>Extant notes created</TableHead>
                    <TableHead>In flight</TableHead>
                    <TableHead>Parked notes</TableHead>
                    <TableHead>Parked items</TableHead>
                    <TableHead>Queued reclassification</TableHead>
                    <TableHead>Stranded parked items</TableHead>
                    <TableHead>Terminal parked awaiting absorption</TableHead>
                    <TableHead>Terminal</TableHead>
                    <TableHead>Absorbed</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Blocked reason</TableHead>
                    <TableHead>Prompt SHA counts</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {organizations.map((organization) => (
                    <TableRow key={organization.organization_id}>
                        <TableCell>
                            <span className="flex flex-col">
                                <span className="font-medium">
                                    {organizationLabel(organization)}
                                </span>
                                <span className="text-muted-foreground font-mono text-xs">
                                    {organization.organization_id}
                                </span>
                            </span>
                        </TableCell>
                        <TableCell>
                            <Badge
                                variant={
                                    organization.enrolled
                                        ? 'default'
                                        : 'secondary'
                                }
                            >
                                {organization.enrolled
                                    ? 'Enrolled'
                                    : 'Not enrolled'}
                            </Badge>
                        </TableCell>
                        <TableCell>
                            <Badge
                                variant={
                                    organization.park_new_items
                                        ? 'default'
                                        : 'secondary'
                                }
                            >
                                {organization.park_new_items
                                    ? 'Parked'
                                    : 'Immediate'}
                            </Badge>
                        </TableCell>
                        <TableCell>{organization.eligible}</TableCell>
                        <TableCell>{organization.notes_created}</TableCell>
                        <TableCell>{organization.in_flight}</TableCell>
                        <TableCell>{organization.parked_notes}</TableCell>
                        <TableCell>
                            <a
                                className="text-primary underline-offset-4 hover:underline"
                                href={parkedItemsHref(
                                    organization.organization_id,
                                )}
                            >
                                {organization.parked_items}
                            </a>
                        </TableCell>
                        <TableCell>
                            {organization.queued_reclassification_count}
                        </TableCell>
                        <TableCell>
                            {organization.stranded_parked_item_count}
                        </TableCell>
                        <TableCell>
                            {organization.terminal_parked_awaiting_absorption}
                        </TableCell>
                        <TableCell>{organization.terminal}</TableCell>
                        <TableCell>{organization.absorbed}</TableCell>
                        <TableCell>{organization.failed}</TableCell>
                        <TableCell className="max-w-72 whitespace-normal">
                            {organization.blocked_reason ===
                            'expanded_memory_inputs_disabled'
                                ? 'Re-enable expanded memory before continuing'
                                : (organization.blocked_reason ?? 'None')}
                        </TableCell>
                        <TableCell>
                            <PromptCounts organization={organization} />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function MembershipDialog({
    action,
    organization,
    isMutating,
    error,
    onOpenChange,
    onConfirm,
}: {
    readonly action: MembershipAction | null;
    readonly organization: MemoryBackfillOrganization | null;
    readonly isMutating: boolean;
    readonly error: string | null;
    readonly onOpenChange: (open: boolean) => void;
    readonly onConfirm: () => void;
}) {
    const actionLabel = action === null ? '' : MEMBERSHIP_LABELS[action];
    const organizationName =
        organization === null ? '' : organizationLabel(organization);
    return (
        <Dialog open={action !== null} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{actionLabel} organization</DialogTitle>
                    <DialogDescription>
                        Confirm {actionLabel.toLowerCase()} for{' '}
                        {organizationName}.
                    </DialogDescription>
                </DialogHeader>
                {action === 'unpark' && (
                    <p className="text-sm">
                        Unpark is allowed only after parked items and queued
                        reclassifications both reach zero.
                    </p>
                )}
                {error !== null && (
                    <p className="text-destructive text-sm">{error}</p>
                )}
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={isMutating}
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button disabled={isMutating} onClick={onConfirm}>
                        {isMutating && <Spinner size="sm" tone="muted" />}
                        Confirm {actionLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** LIVE-only operator control plane for the parked memory backfill. */
export function MemoryBackfillTab() {
    const memoryBackfill = useMemoryBackfill();
    const [membershipTarget, setMembershipTarget] =
        useState<MembershipTarget | null>(null);
    const [releaseOpen, setReleaseOpen] = useState(false);
    const [reclassifyOpen, setReclassifyOpen] = useState(false);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [selectedDomains, setSelectedDomains] = useState<
        Array<AgentNoteDomain>
    >(() => DOMAIN_OPTIONS.map((option) => option.value));
    const [releaseLimit, setReleaseLimit] = useState(200);
    const [reclassifyLimit, setReclassifyLimit] = useState(200);
    const [releaseResult, setReleaseResult] = useState<Awaited<
        ReturnType<typeof memoryBackfill.release>
    > | null>(null);
    const [reclassifyResult, setReclassifyResult] = useState<Awaited<
        ReturnType<typeof memoryBackfill.reclassify>
    > | null>(null);

    const blockedRows = useMemo(
        () =>
            memoryBackfill.blockedOrganizations.map((organization) =>
                blockedOrganizationRow(
                    organization.organization_id,
                    organization.organization_name,
                ),
            ),
        [memoryBackfill.blockedOrganizations],
    );
    const organizations = memoryBackfill.organizations;
    const selectedOrganization =
        organizations.find(
            (organization) =>
                organization.organization_id ===
                memoryBackfill.selectedOrganizationId,
        ) ??
        blockedRows.find(
            (organization) =>
                organization.organization_id ===
                memoryBackfill.selectedOrganizationId,
        ) ??
        null;
    const selectedOrganizationId = memoryBackfill.selectedOrganizationId;
    const isBlocked =
        selectedOrganization?.blocked_reason ===
        'expanded_memory_inputs_disabled';
    const canReview =
        selectedOrganization !== null &&
        !selectedOrganization.enrolled &&
        selectedOrganization.park_new_items;
    const unparkBlocked =
        selectedOrganization !== null &&
        (selectedOrganization.parked_items > 0 ||
            selectedOrganization.queued_reclassification_count > 0);
    const validReleaseLimit = releaseLimit >= 1 && releaseLimit <= 500;
    const validReclassifyLimit = reclassifyLimit >= 1 && reclassifyLimit <= 500;
    const selectedLabel =
        selectedOrganization === null
            ? (selectedOrganizationId ?? '')
            : organizationLabel(selectedOrganization);
    const releaseScope =
        selectedDomains.length === DOMAIN_OPTIONS.length
            ? 'all domains'
            : DOMAIN_OPTIONS.filter((option) =>
                  selectedDomains.includes(option.value),
              )
                  .map((option) => option.label)
                  .join(', ');
    const strandedOrganizations = organizations.filter(
        (organization) => organization.stranded_parked_item_count > 0,
    );

    async function confirmMembership() {
        if (membershipTarget === null) {
            return;
        }
        const organizationId = membershipTarget.organization.organization_id;
        const organizationIds = [organizationId];
        const result =
            membershipTarget.action === 'enroll'
                ? await memoryBackfill.enroll(organizationId)
                : membershipTarget.action === 'remove'
                  ? await memoryBackfill.remove(organizationIds)
                  : membershipTarget.action === 'park'
                    ? await memoryBackfill.park(organizationIds)
                    : await memoryBackfill.unpark(organizationIds);
        if (result !== null) {
            setMembershipTarget(null);
        }
    }

    async function confirmRelease() {
        if (selectedOrganizationId === undefined) {
            return;
        }
        const result = await memoryBackfill.release({
            organization_id: selectedOrganizationId,
            domains:
                selectedDomains.length === DOMAIN_OPTIONS.length
                    ? null
                    : selectedDomains,
            note_ids: null,
            limit: releaseLimit,
        });
        setReleaseResult(result);
    }

    async function confirmReclassify() {
        if (selectedOrganizationId === undefined) {
            return;
        }
        const result = await memoryBackfill.reclassify({
            organization_id: selectedOrganizationId,
            note_ids: null,
            limit: reclassifyLimit,
        });
        setReclassifyResult(result);
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <AdminOrgPicker
                        value={selectedOrganizationId}
                        onValueChange={memoryBackfill.selectOrganization}
                    />
                    <Badge variant="outline">Live only</Badge>
                    {memoryBackfill.isRefreshing && (
                        <Spinner size="sm" tone="muted" />
                    )}
                </div>
                <Button
                    variant="outline"
                    disabled={memoryBackfill.isRefreshing}
                    onClick={() => {
                        void memoryBackfill.refresh();
                    }}
                >
                    {memoryBackfill.isRefreshing && (
                        <Spinner size="sm" tone="muted" />
                    )}
                    Refresh
                </Button>
            </div>
            {blockedRows.map((organization) => (
                <div
                    key={organization.organization_id}
                    className="border-destructive/40 bg-destructive/5 text-destructive flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm"
                >
                    <span className="flex min-w-0 flex-col">
                        <span>
                            {organizationLabel(organization)}: Re-enable
                            expanded memory before continuing
                        </span>
                        <span className="font-mono text-xs">
                            {organization.organization_id}
                        </span>
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Remove ${organizationLabel(organization)}`}
                        disabled={memoryBackfill.isMutating}
                        onClick={() => {
                            memoryBackfill.clearMutationError();
                            setMembershipTarget({
                                action: 'remove',
                                organization,
                            });
                        }}
                    >
                        Remove
                    </Button>
                </div>
            ))}
            {strandedOrganizations.map((organization) => (
                <p
                    key={organization.organization_id}
                    className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
                >
                    {organizationLabel(organization)} has{' '}
                    {organization.stranded_parked_item_count} stranded parked
                    items. The pipeline will not process them automatically.
                    Re-add this organization to the park list before releasing
                    or discarding.
                </p>
            ))}
            {memoryBackfill.previewError !== null && (
                <p className="text-destructive rounded-md border px-3 py-6 text-center text-sm">
                    {memoryBackfill.previewError}
                </p>
            )}
            {memoryBackfill.isLoading && organizations.length === 0 ? (
                <p className="text-muted-foreground flex items-center justify-center gap-2 rounded-md border px-3 py-12 text-sm">
                    <Spinner size="sm" tone="muted" />
                    Loading memory backfill preview…
                </p>
            ) : organizations.length === 0 && blockedRows.length === 0 ? (
                <p className="text-muted-foreground rounded-md border px-3 py-12 text-center text-sm">
                    Select an organization to preview it, or enroll an
                    organization to begin.
                </p>
            ) : organizations.length > 0 ? (
                <ProgressTable organizations={organizations} />
            ) : null}
            <div className="flex flex-wrap gap-2">
                <Button
                    disabled={
                        selectedOrganizationId === undefined ||
                        selectedOrganization === null ||
                        selectedOrganization?.enrolled === true ||
                        isBlocked ||
                        memoryBackfill.isMutating
                    }
                    onClick={() => {
                        if (selectedOrganization !== null) {
                            setMembershipTarget({
                                action: 'enroll',
                                organization: selectedOrganization,
                            });
                        }
                    }}
                >
                    Enroll
                </Button>
                <Button
                    variant="outline"
                    disabled={
                        selectedOrganizationId === undefined ||
                        selectedOrganization === null ||
                        memoryBackfill.isMutating
                    }
                    onClick={() => {
                        if (selectedOrganization !== null) {
                            setMembershipTarget({
                                action: 'remove',
                                organization: selectedOrganization,
                            });
                        }
                    }}
                >
                    Remove
                </Button>
                <Button
                    variant="outline"
                    disabled={
                        selectedOrganizationId === undefined ||
                        selectedOrganization === null ||
                        selectedOrganization?.park_new_items === true ||
                        memoryBackfill.isMutating
                    }
                    onClick={() => {
                        if (selectedOrganization !== null) {
                            setMembershipTarget({
                                action: 'park',
                                organization: selectedOrganization,
                            });
                        }
                    }}
                >
                    Park
                </Button>
                <Button
                    variant="outline"
                    disabled={
                        selectedOrganizationId === undefined ||
                        selectedOrganization === null ||
                        selectedOrganization?.park_new_items !== true ||
                        unparkBlocked ||
                        memoryBackfill.isMutating
                    }
                    onClick={() => {
                        if (selectedOrganization !== null) {
                            setMembershipTarget({
                                action: 'unpark',
                                organization: selectedOrganization,
                            });
                        }
                    }}
                >
                    Unpark
                </Button>
                <Button
                    variant="outline"
                    disabled={!canReview || memoryBackfill.isMutating}
                    onClick={() => {
                        memoryBackfill.clearMutationError();
                        setReleaseResult(null);
                        setReleaseOpen(true);
                    }}
                >
                    Release
                </Button>
                <Button
                    variant="outline"
                    disabled={!canReview || memoryBackfill.isMutating}
                    onClick={() => {
                        memoryBackfill.clearMutationError();
                        setReclassifyResult(null);
                        setReclassifyOpen(true);
                    }}
                >
                    Re-classify parked
                </Button>
                <Button
                    variant="destructive"
                    disabled={!canReview}
                    onClick={() => {
                        setDiscardOpen(true);
                    }}
                >
                    Discard
                </Button>
            </div>
            {selectedOrganizationId !== undefined && (
                <div className="flex flex-wrap gap-4 text-sm">
                    <a
                        className="text-primary underline-offset-4 hover:underline"
                        href={parkedItemsHref(selectedOrganizationId)}
                    >
                        Review parked items
                    </a>
                    <a
                        className="text-primary underline-offset-4 hover:underline"
                        href={zeroItemsHref(selectedOrganizationId)}
                    >
                        Review zero-item notes
                    </a>
                </div>
            )}
            <MembershipDialog
                action={membershipTarget?.action ?? null}
                organization={membershipTarget?.organization ?? null}
                isMutating={memoryBackfill.isMutating}
                error={memoryBackfill.mutationError}
                onOpenChange={(open) => {
                    if (!open) {
                        setMembershipTarget(null);
                        memoryBackfill.clearMutationError();
                    }
                }}
                onConfirm={() => {
                    void confirmMembership();
                }}
            />
            <Dialog
                open={releaseOpen}
                onOpenChange={(open) => {
                    setReleaseOpen(open);
                    if (!open) {
                        memoryBackfill.clearMutationError();
                        setReleaseResult(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Release parked items</DialogTitle>
                        <DialogDescription>
                            Release up to {releaseLimit} items for{' '}
                            {selectedLabel} across {releaseScope}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-3">
                        <div className="grid gap-2">
                            {DOMAIN_OPTIONS.map((option) => (
                                <label
                                    key={option.value}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <Checkbox
                                        checked={selectedDomains.includes(
                                            option.value,
                                        )}
                                        onCheckedChange={(checked) => {
                                            setSelectedDomains((previous) =>
                                                checked === true
                                                    ? [
                                                          ...previous,
                                                          option.value,
                                                      ]
                                                    : previous.filter(
                                                          (domain) =>
                                                              domain !==
                                                              option.value,
                                                      ),
                                            );
                                        }}
                                    />
                                    {option.label}
                                </label>
                            ))}
                        </div>
                        <label className="grid gap-1 text-sm">
                            Batch size
                            <Input
                                aria-label="Release batch size"
                                type="number"
                                min={1}
                                max={500}
                                value={releaseLimit}
                                onChange={(event) => {
                                    setReleaseLimit(
                                        Number(event.currentTarget.value),
                                    );
                                }}
                            />
                        </label>
                        <p className="text-muted-foreground text-sm">
                            Current organization total:{' '}
                            {selectedOrganization?.parked_items ?? 0} parked
                            items.
                        </p>
                        {releaseResult !== null && (
                            <p className="text-sm">
                                Released {releaseResult.released_item_count}{' '}
                                items. Remaining in request scope:{' '}
                                {releaseResult.remaining_parked_item_count}.
                                Remaining across the organization:{' '}
                                {
                                    releaseResult.organization_remaining_parked_item_count
                                }
                                . Failed to publish:{' '}
                                {releaseResult.failed_publish_item_ids
                                    .length === 0
                                    ? '0'
                                    : releaseResult.failed_publish_item_ids.join(
                                          ', ',
                                      )}
                                .
                            </p>
                        )}
                        {memoryBackfill.mutationError !== null && (
                            <p className="text-destructive text-sm">
                                {memoryBackfill.mutationError}
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={memoryBackfill.isMutating}
                            onClick={() => {
                                setReleaseOpen(false);
                            }}
                        >
                            {releaseResult === null ? 'Cancel' : 'Close'}
                        </Button>
                        {releaseResult === null && (
                            <Button
                                disabled={
                                    memoryBackfill.isMutating ||
                                    !validReleaseLimit ||
                                    selectedDomains.length === 0
                                }
                                onClick={() => {
                                    void confirmRelease();
                                }}
                            >
                                {memoryBackfill.isMutating && (
                                    <Spinner size="sm" tone="muted" />
                                )}
                                Confirm release
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog
                open={reclassifyOpen}
                onOpenChange={(open) => {
                    setReclassifyOpen(open);
                    if (!open) {
                        memoryBackfill.clearMutationError();
                        setReclassifyResult(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Re-classify parked notes</DialogTitle>
                        <DialogDescription>
                            Re-classify up to {reclassifyLimit} eligible parked
                            notes for {selectedLabel} using the current model
                            and prompt.
                        </DialogDescription>
                    </DialogHeader>
                    <label className="grid gap-1 text-sm">
                        Batch size
                        <Input
                            aria-label="Re-classification batch size"
                            type="number"
                            min={1}
                            max={500}
                            value={reclassifyLimit}
                            onChange={(event) => {
                                setReclassifyLimit(
                                    Number(event.currentTarget.value),
                                );
                            }}
                        />
                    </label>
                    {reclassifyResult !== null && (
                        <p className="text-sm">
                            Queued {reclassifyResult.reclassified_note_count}{' '}
                            notes. Remaining eligible parked notes:{' '}
                            {reclassifyResult.remaining_parked_note_count}.
                            Failed to publish:{' '}
                            {reclassifyResult.failed_publish_note_ids.length ===
                            0
                                ? '0'
                                : reclassifyResult.failed_publish_note_ids.join(
                                      ', ',
                                  )}
                            .
                        </p>
                    )}
                    {memoryBackfill.mutationError !== null && (
                        <p className="text-destructive text-sm">
                            {memoryBackfill.mutationError}
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={memoryBackfill.isMutating}
                            onClick={() => {
                                setReclassifyOpen(false);
                            }}
                        >
                            {reclassifyResult === null ? 'Cancel' : 'Close'}
                        </Button>
                        {reclassifyResult === null && (
                            <Button
                                disabled={
                                    memoryBackfill.isMutating ||
                                    !validReclassifyLimit
                                }
                                onClick={() => {
                                    void confirmReclassify();
                                }}
                            >
                                {memoryBackfill.isMutating && (
                                    <Spinner size="sm" tone="muted" />
                                )}
                                Confirm re-classification
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Discard reviewed work</DialogTitle>
                        <DialogDescription>
                            Open a scoped Notes view for {selectedLabel}, select
                            reviewed notes, then use Discard selected. Notes
                            with a released sibling discard only their eligible
                            unreleased parked items.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedOrganizationId !== undefined && (
                        <div className="flex flex-col gap-2 text-sm">
                            <a
                                className="text-primary underline-offset-4 hover:underline"
                                href={parkedItemsHref(selectedOrganizationId)}
                            >
                                Open parked-item view
                            </a>
                            <a
                                className="text-primary underline-offset-4 hover:underline"
                                href={zeroItemsHref(selectedOrganizationId)}
                            >
                                Open zero-item view
                            </a>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setDiscardOpen(false);
                            }}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
